/**
 * References Map - Maison Louis Drucker
 * Interactive map displaying references/locations with filtering and detail panel
 */

(function() {
  // Prevent multiple declarations
  if (window.ReferencesMapInitialized) return;
  window.ReferencesMapInitialized = true;

  class ReferencesMap {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.section = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (!this.section) return;

      this.mapContainer = document.getElementById(`references-map-${sectionId}`);
      this.panel = document.getElementById(`references-panel-${sectionId}`);
      this.gridContainer = document.getElementById(`references-grid-${sectionId}`);
      this.dataElement = document.getElementById(`references-data-${sectionId}`);
      this.countElement = this.section.querySelector('.references-map__count-number');
      this.currentView = 'map';

      if (!this.mapContainer || !this.dataElement) return;

      this.data = JSON.parse(this.dataElement.textContent);
      this.map = null;
      this.markers = [];
      this.activeMarker = null;
      this.overlay = null;
      this.clusterSourceId = `references-cluster-source-${sectionId}`;
      this.clusterLayerId = `clusters-${sectionId}`;
      this.clusterCountLayerId = `cluster-count-${sectionId}`;
      this.renderHandler = null;

      // Filtres multiples
      this.filters = {
        category: 'all',
        countries: ['all'] // Array pour multiselect
      };

      this.init();
    }

    init() {
      if (!this.data.config.mapboxToken) {
        console.warn('References Map: Mapbox token is missing');
        this.showError('Configuration Mapbox manquante');
        return;
      }

      this.createOverlay();
      this.initMap();
      this.bindEvents();
    }

    createOverlay() {
      this.overlay = document.createElement('div');
      this.overlay.className = 'references-map__overlay';
      const container = this.section.querySelector('.references-map__container');
      if (container) {
        container.appendChild(this.overlay);
      }
    }

    initMap() {
      mapboxgl.accessToken = this.data.config.mapboxToken;

      // Parse string coordinates to numbers with validation
      const defaultLat = this.parseCoordinate(this.data.config.defaultLat, 48.8566);
      const defaultLng = this.parseCoordinate(this.data.config.defaultLng, 2.3522);

      this.map = new mapboxgl.Map({
        container: this.mapContainer,
        style: this.data.config.mapStyle,
        center: [defaultLng, defaultLat],
        zoom: this.data.config.defaultZoom || 5,
        projection: 'mercator' // Vue carte plate (pas de globe 3D)
      });

      this.map.addControl(new mapboxgl.NavigationControl(), 'top-left');

      this.map.on('load', () => {
        this.addMarkers();
      });

      // Close panel when clicking on map
      this.map.on('click', (e) => {
        // Check if click is on a marker or cluster
        if (e.originalEvent.target.closest('.references-map__marker')) return;

        // Check if click is on cluster layer
        const clusterFeatures = this.map.queryRenderedFeatures(e.point, {
          layers: [this.clusterLayerId]
        });
        if (clusterFeatures.length > 0) return;

        this.closePanel();
      });
    }

    parseCoordinate(value, fallback) {
      if (value === null || value === undefined || value === '') return fallback;
      const parsed = parseFloat(value);
      if (isNaN(parsed)) return fallback;
      return parsed;
    }

    isValidCoordinate(lat, lng) {
      return typeof lat === 'number' &&
             typeof lng === 'number' &&
             !isNaN(lat) &&
             !isNaN(lng) &&
             lat >= -90 && lat <= 90 &&
             lng >= -180 && lng <= 180;
    }

    addMarkers() {
      // Clear existing markers
      this.markers.forEach(m => {
        if (m.marker) m.marker.remove();
      });
      this.markers = [];

      // Remove render handler if exists
      if (this.renderHandler) {
        this.map.off('render', this.renderHandler);
        this.renderHandler = null;
      }

      // Remove existing cluster layers and source
      if (this.map.getLayer(this.clusterLayerId)) {
        this.map.removeLayer(this.clusterLayerId);
      }
      if (this.map.getLayer(this.clusterCountLayerId)) {
        this.map.removeLayer(this.clusterCountLayerId);
      }
      if (this.map.getSource(this.clusterSourceId)) {
        this.map.removeSource(this.clusterSourceId);
      }

      const references = this.getFilteredReferences();

      // Update count
      if (this.countElement) {
        this.countElement.textContent = references.length;
      }

      // Build GeoJSON data with validated coordinates
      const features = [];
      references.forEach(ref => {
        const lat = this.parseCoordinate(ref.lat, null);
        const lng = this.parseCoordinate(ref.lng, null);

        if (!this.isValidCoordinate(lat, lng)) {
          console.warn(`Invalid coordinates for reference ${ref.id}:`, { lat: ref.lat, lng: ref.lng });
          return;
        }

        features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lng, lat]
          },
          properties: {
            id: ref.id,
            lat,
            lng
          }
        });
      });

      const geojsonData = {
        type: 'FeatureCollection',
        features
      };

      if (features.length === 0) {
        return;
      }

      // Add clustered source
      this.map.addSource(this.clusterSourceId, {
        type: 'geojson',
        data: geojsonData,
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 20
      });

      // Add cluster circles layer
      this.map.addLayer({
        id: this.clusterLayerId,
        type: 'circle',
        source: this.clusterSourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#b8860b',
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,   // radius for count < 10
            10, 25, // radius for count >= 10
            30, 30  // radius for count >= 30
          ],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#fff'
        }
      });

      // Add cluster count text layer
      this.map.addLayer({
        id: this.clusterCountLayerId,
        type: 'symbol',
        source: this.clusterSourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 14
        },
        paint: {
          'text-color': '#ffffff'
        }
      });

      // Store geojson for later use
      this.currentGeoJSON = geojsonData;

      // Create render handler
      this.renderHandler = () => {
        if (!this.map.isSourceLoaded(this.clusterSourceId)) return;
        this.updateVisibleMarkers();
      };

      // Update markers on render (for cluster/uncluster transitions)
      this.map.on('render', this.renderHandler);

      // Click on cluster to zoom in
      this.map.on('click', this.clusterLayerId, (e) => {
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: [this.clusterLayerId]
        });
        if (!features.length) return;
        const clusterId = features[0].properties.cluster_id;
        this.map.getSource(this.clusterSourceId).getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
            if (err) return;
            this.map.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom
            });
          }
        );
      });

      // Change cursor on cluster hover
      this.map.on('mouseenter', this.clusterLayerId, () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', this.clusterLayerId, () => {
        this.map.getCanvas().style.cursor = '';
      });

      // Fit bounds if there are features
      if (geojsonData.features.length > 0) {
        this.fitBoundsToFeatures(geojsonData.features);
      }

      // Initial marker update
      this.updateVisibleMarkers();
    }

    updateVisibleMarkers() {
      if (!this.map.getSource(this.clusterSourceId)) return;

      // Get all unclustered points currently visible
      const features = this.map.querySourceFeatures(this.clusterSourceId, {
        filter: ['!', ['has', 'point_count']]
      });

      // Create a set of currently visible point IDs
      const visibleIds = new Set(features.map(f => f.properties.id));

      // Remove markers that are no longer visible (clustered)
      this.markers = this.markers.filter(m => {
        if (!visibleIds.has(m.data.id)) {
          m.marker.remove();
          return false;
        }
        return true;
      });

      // Get IDs of existing markers
      const existingIds = new Set(this.markers.map(m => m.data.id));

      // Add markers for newly visible points
      features.forEach(feature => {
        const refId = feature.properties.id;
        if (existingIds.has(refId)) return;

        const coords = feature.geometry.coordinates;
        if (!coords || coords.length < 2) return;

        const lng = coords[0];
        const lat = coords[1];

        if (!this.isValidCoordinate(lat, lng)) return;

        // Get full reference data
        const refData = this.data.references.find(r => r.id === refId) || { id: refId };

        const el = document.createElement('div');
        el.className = 'references-map__marker';
        if (refData.product_url) {
          el.classList.add('has-product');
        }
        el.dataset.refId = refId;

        // Marker icon
        let markerHtml = `
          <svg class="references-map__marker-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        `;

        // Add product tooltip if product exists
        if (refData.product_url && refData.product_image) {
          markerHtml += `
            <div class="references-map__marker-tooltip">
              <img src="${refData.product_image}" alt="${this.escapeHtml(refData.product_title || '')}" class="references-map__marker-tooltip-image">
              <span class="references-map__marker-tooltip-label">${this.escapeHtml(refData.product_title || 'Voir le produit')}</span>
            </div>
          `;
        }

        el.innerHTML = markerHtml;

        el.addEventListener('click', (e) => {
          e.stopPropagation();
          this.selectReference({ ...refData, lat, lng }, el);
        });

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([lng, lat])
          .addTo(this.map);

        this.markers.push({ marker, element: el, data: { id: refId, lat, lng } });
      });
    }

    getFilteredReferences() {
      return this.data.references.filter(ref => {
        // Filtre par catégorie
        if (this.filters.category !== 'all') {
          const refCatHandle = (ref.categoryHandle || '').toLowerCase().trim();
          if (refCatHandle !== this.filters.category.toLowerCase().trim()) {
            return false;
          }
        }

        // Filtre par pays (multiselect)
        if (!this.filters.countries.includes('all')) {
          const refCountryHandle = (ref.countryHandle || '').toLowerCase().trim();
          if (!this.filters.countries.includes(refCountryHandle)) {
            return false;
          }
        }

        return true;
      });
    }

    fitBoundsToFeatures(features) {
      if (features.length === 0) return;

      if (features.length === 1) {
        const coords = features[0].geometry.coordinates;
        if (coords && coords.length >= 2) {
          this.map.flyTo({
            center: coords,
            zoom: 12
          });
        }
        return;
      }

      const bounds = new mapboxgl.LngLatBounds();
      features.forEach(f => {
        if (f.geometry.coordinates && f.geometry.coordinates.length >= 2) {
          bounds.extend(f.geometry.coordinates);
        }
      });

      this.map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 420 },
        maxZoom: 14
      });
    }

    selectReference(ref, markerElement) {
      // Update active marker
      if (this.activeMarker) {
        this.activeMarker.classList.remove('active');
      }
      markerElement.classList.add('active');
      this.activeMarker = markerElement;

      // Update panel content
      this.updatePanel(ref);
      this.openPanel();

      // Pan to marker
      const lat = this.parseCoordinate(ref.lat, null);
      const lng = this.parseCoordinate(ref.lng, null);

      if (this.isValidCoordinate(lat, lng)) {
        this.map.flyTo({
          center: [lng, lat],
          zoom: Math.max(this.map.getZoom(), 12),
          offset: window.innerWidth > 768 ? [-150, 0] : [0, -100]
        });
      }
    }

    updatePanel(ref) {
      const content = this.panel.querySelector('.references-map__panel-content');

      // Product thumbnail overlay on image
      const productOverlayHtml = (ref.product_url && ref.product_image)
        ? `<a href="${ref.product_url}" class="references-map__panel-image-product">
             <img src="${ref.product_image}" alt="${this.escapeHtml(ref.product_title || '')}" loading="lazy">
           </a>`
        : '';

      const imageHtml = ref.image
        ? `<div class="references-map__panel-image-wrapper">
             <img src="${ref.image}" alt="${this.escapeHtml(ref.name || '')}" class="references-map__panel-image" loading="lazy">
             ${productOverlayHtml}
           </div>`
        : `<div class="references-map__panel-image-wrapper">
             <div class="references-map__panel-image-placeholder">
               <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                 <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                 <circle cx="8.5" cy="8.5" r="1.5"></circle>
                 <polyline points="21 15 16 10 5 21"></polyline>
               </svg>
             </div>
             ${productOverlayHtml}
           </div>`;

      // Format description - handle richtext HTML
      let descriptionHtml = '';
      if (ref.description) {
        // Si c'est du HTML (richtext), l'utiliser directement
        if (typeof ref.description === 'string' && ref.description.includes('<')) {
          descriptionHtml = ref.description;
        } else {
          descriptionHtml = `<p>${this.escapeHtml(ref.description)}</p>`;
        }
      }

      // Build address
      let addressParts = [];
      if (ref.address) addressParts.push(ref.address);
      if (ref.city) addressParts.push(ref.city);
      if (ref.country) addressParts.push(ref.country);
      const fullAddress = addressParts.join(', ');

      let linksHtml = '';

      if (fullAddress) {
        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
        linksHtml += `
          <a href="${mapsUrl}" target="_blank" rel="noopener" class="references-map__panel-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <span>${this.escapeHtml(fullAddress)}</span>
          </a>
        `;
      }

      if (ref.website && typeof ref.website === 'string') {
        // S'assurer que l'URL est absolue (commence par http:// ou https://)
        let websiteUrl = ref.website.trim();
        if (websiteUrl && !websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
          websiteUrl = 'https://' + websiteUrl;
        }
        linksHtml += `
          <a href="${websiteUrl}" target="_blank" rel="noopener" class="references-map__panel-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="2" y1="12" x2="22" y2="12"></line>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
            <span>Visiter le site</span>
          </a>
        `;
      }

      if (ref.instagram && typeof ref.instagram === 'string' && !ref.instagram.includes('[object')) {
        let instaUrl = '';
        let instaDisplay = '';

        // Check if it's already a full URL
        if (ref.instagram.includes('instagram.com')) {
          instaUrl = ref.instagram;
          // Extract handle from URL for display
          const match = ref.instagram.match(/instagram\.com\/([^\/\?]+)/);
          instaDisplay = match ? `@${match[1]}` : 'Instagram';
        } else {
          // It's just a handle
          const instaHandle = ref.instagram.replace('@', '').trim();
          instaUrl = `https://instagram.com/${instaHandle}`;
          instaDisplay = `@${instaHandle}`;
        }

        if (instaUrl) {
          linksHtml += `
            <a href="${instaUrl}" target="_blank" rel="noopener" class="references-map__panel-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
              <span>${this.escapeHtml(instaDisplay)}</span>
            </a>
          `;
        }
      }

      if (ref.product_url) {
        const productImageHtml = ref.product_image
          ? `<img src="${ref.product_image}" alt="${this.escapeHtml(ref.product_title || '')}" class="references-map__panel-product-image">`
          : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>`;
        linksHtml += `
          <a href="${ref.product_url}" class="references-map__panel-link references-map__panel-link--product">
            ${productImageHtml}
            <span>${this.escapeHtml(ref.product_title || 'Voir le produit')}</span>
          </a>
        `;
      }

      content.innerHTML = `
        ${imageHtml}
        <div class="references-map__panel-body">
          ${ref.category ? `<span class="references-map__panel-category">${this.escapeHtml(ref.category)}</span>` : ''}
          <h3 class="references-map__panel-name">${this.escapeHtml(ref.name || 'Sans nom')}</h3>
          ${descriptionHtml ? `<div class="references-map__panel-description">${descriptionHtml}</div>` : ''}
          ${linksHtml ? `<div class="references-map__panel-links">${linksHtml}</div>` : ''}
        </div>
      `;
    }

    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    openPanel() {
      this.panel.classList.add('open');
      this.overlay.classList.add('visible');
      document.body.style.overflow = window.innerWidth <= 768 ? 'hidden' : '';
    }

    closePanel() {
      this.panel.classList.remove('open');
      this.overlay.classList.remove('visible');
      document.body.style.overflow = '';

      if (this.activeMarker) {
        this.activeMarker.classList.remove('active');
        this.activeMarker = null;
      }
    }

    setFilter(type, value) {
      this.filters[type] = value;
      this.closePanel();
      this.addMarkers();
      // Also update grid if in grid view
      if (this.currentView === 'grid') {
        this.renderGrid();
      }
    }

    bindEvents() {
      // View switch (carte/grille)
      const viewBtns = this.section.querySelectorAll('.references-map__view-btn');
      viewBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const view = btn.dataset.view;
          this.switchView(view);
        });
      });

      // Filter buttons pour catégories
      const filterBtns = this.section.querySelectorAll('.references-map__filter-btn');
      filterBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const filterType = btn.dataset.filterType;
          const filterValue = btn.dataset.filterValue;

          // Update active state only for buttons of same type
          this.section.querySelectorAll(`[data-filter-type="${filterType}"]`).forEach(b => {
            b.classList.remove('active');
          });
          btn.classList.add('active');

          this.setFilter(filterType, filterValue);
        });
      });

      // Dropdown multiselect pour pays
      this.initCountryDropdown();

      // Close panel button
      const closeBtn = this.panel.querySelector('.references-map__panel-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closePanel());
      }

      // Overlay click
      this.overlay.addEventListener('click', () => this.closePanel());

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closePanel();
          // Fermer aussi le dropdown si ouvert
          const dropdown = this.section.querySelector('.references-map__dropdown');
          if (dropdown) {
            dropdown.classList.remove('open');
            dropdown.querySelector('.references-map__dropdown-toggle').setAttribute('aria-expanded', 'false');
          }
        }
      });

      // Click outside to close dropdown
      document.addEventListener('click', (e) => {
        const dropdown = this.section.querySelector('.references-map__dropdown');
        if (dropdown && !dropdown.contains(e.target)) {
          dropdown.classList.remove('open');
          dropdown.querySelector('.references-map__dropdown-toggle').setAttribute('aria-expanded', 'false');
        }
      });
    }

    initCountryDropdown() {
      const dropdown = this.section.querySelector('.references-map__dropdown[data-filter-type="country"]');
      if (!dropdown) return;

      const toggle = dropdown.querySelector('.references-map__dropdown-toggle');
      const menu = dropdown.querySelector('.references-map__dropdown-menu');
      const label = dropdown.querySelector('.references-map__dropdown-label');
      const checkboxes = menu.querySelectorAll('input[type="checkbox"]');
      const allCheckbox = menu.querySelector('input[value="all"]');

      // Toggle dropdown
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = dropdown.classList.toggle('open');
        toggle.setAttribute('aria-expanded', isOpen);
      });

      // Handle checkbox changes
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          if (checkbox.value === 'all') {
            // Si "Tous" est coché, décocher les autres
            if (checkbox.checked) {
              checkboxes.forEach(cb => {
                if (cb.value !== 'all') cb.checked = false;
              });
              this.filters.countries = ['all'];
            } else {
              // Empêcher de décocher "Tous" si aucun autre n'est sélectionné
              const otherChecked = Array.from(checkboxes).some(cb => cb.value !== 'all' && cb.checked);
              if (!otherChecked) {
                checkbox.checked = true;
                return;
              }
            }
          } else {
            // Si un pays spécifique est coché
            if (checkbox.checked) {
              // Décocher "Tous"
              allCheckbox.checked = false;
              // Ajouter le pays au filtre
              this.filters.countries = this.filters.countries.filter(c => c !== 'all');
              if (!this.filters.countries.includes(checkbox.value)) {
                this.filters.countries.push(checkbox.value);
              }
            } else {
              // Retirer le pays du filtre
              this.filters.countries = this.filters.countries.filter(c => c !== checkbox.value);
              // Si plus aucun pays sélectionné, cocher "Tous"
              if (this.filters.countries.length === 0) {
                allCheckbox.checked = true;
                this.filters.countries = ['all'];
              }
            }
          }

          // Mettre à jour le label
          this.updateCountryDropdownLabel(label, checkboxes);

          // Appliquer le filtre
          this.closePanel();
          this.addMarkers();
          // Also update grid if in grid view
          if (this.currentView === 'grid') {
            this.renderGrid();
          }
        });
      });
    }

    updateCountryDropdownLabel(label, checkboxes) {
      const selectedCountries = Array.from(checkboxes)
        .filter(cb => cb.checked && cb.value !== 'all')
        .map(cb => cb.parentElement.querySelector('span').textContent);

      if (this.filters.countries.includes('all') || selectedCountries.length === 0) {
        label.textContent = 'Tous les pays';
      } else if (selectedCountries.length === 1) {
        label.textContent = selectedCountries[0];
      } else if (selectedCountries.length <= 2) {
        label.textContent = selectedCountries.join(', ');
      } else {
        label.textContent = `${selectedCountries.length} pays sélectionnés`;
      }
    }

    switchView(view) {
      if (view === this.currentView) return;

      this.currentView = view;

      // Update switch buttons
      this.section.querySelectorAll('.references-map__view-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.view === view);
      });

      // Toggle views
      const mapView = this.section.querySelector('.references-map__view--map');
      const gridView = this.section.querySelector('.references-map__view--grid');

      if (view === 'map') {
        mapView.classList.add('active');
        gridView.classList.remove('active');
        // Resize map when switching back
        if (this.map) {
          setTimeout(() => this.map.resize(), 100);
        }
      } else {
        mapView.classList.remove('active');
        gridView.classList.add('active');
        this.renderGrid();
      }

      this.closePanel();
    }

    renderGrid() {
      if (!this.gridContainer) return;

      const grid = this.gridContainer.querySelector('.references-map__grid');
      if (!grid) return;

      const references = this.getFilteredReferences();

      if (references.length === 0) {
        grid.innerHTML = `
          <div class="references-map__grid-empty">
            <p>Aucune référence trouvée pour ces critères.</p>
          </div>
        `;
        return;
      }

      grid.innerHTML = references.map(ref => this.renderCard(ref)).join('');

      // Add click handlers to cards
      grid.querySelectorAll('.references-map__card').forEach(card => {
        card.addEventListener('click', () => {
          const refId = card.dataset.refId;
          const ref = this.data.references.find(r => r.id === refId);
          if (ref) {
            this.showCardDetail(ref);
          }
        });
      });
    }

    renderCard(ref) {
      const imageHtml = ref.image
        ? `<img src="${ref.image}" alt="${this.escapeHtml(ref.name || '')}" class="references-map__card-image" loading="lazy">`
        : `<div class="references-map__card-image-placeholder">
             <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
               <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
               <circle cx="8.5" cy="8.5" r="1.5"></circle>
               <polyline points="21 15 16 10 5 21"></polyline>
             </svg>
           </div>`;

      let locationParts = [];
      if (ref.city) locationParts.push(ref.city);
      if (ref.country) locationParts.push(ref.country);
      const location = locationParts.join(', ');

      // Extract text from richtext HTML for description preview
      let descriptionText = '';
      if (ref.description) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = ref.description;
        descriptionText = tempDiv.textContent || tempDiv.innerText || '';
      }

      // Product thumbnail for hover display (clickable link to product page)
      const productHtml = (ref.product_url && ref.product_image)
        ? `<a href="${ref.product_url}" class="references-map__card-product" onclick="event.stopPropagation();">
             <img src="${ref.product_image}" alt="${this.escapeHtml(ref.product_title || 'Produit')}" class="references-map__card-product-image" loading="lazy">
             <span class="references-map__card-product-label">${this.escapeHtml(ref.product_title || 'Voir le produit')}</span>
           </a>`
        : '';

      return `
        <div class="references-map__card${productHtml ? ' has-product' : ''}" data-ref-id="${ref.id}">
          <div class="references-map__card-image-wrapper">
            ${imageHtml}
            ${productHtml}
          </div>
          <div class="references-map__card-body">
            ${ref.category ? `<span class="references-map__card-category">${this.escapeHtml(ref.category)}</span>` : ''}
            <h3 class="references-map__card-name">${this.escapeHtml(ref.name || 'Sans nom')}</h3>
            ${location ? `
              <p class="references-map__card-location">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                ${this.escapeHtml(location)}
              </p>
            ` : ''}
            ${descriptionText ? `<p class="references-map__card-description">${this.escapeHtml(descriptionText)}</p>` : ''}
          </div>
        </div>
      `;
    }

    showCardDetail(ref) {
      this.updatePanel(ref);
      this.openPanel();
    }

    showError(message) {
      this.mapContainer.innerHTML = `
        <div class="references-map__no-results">
          <p>${message}</p>
        </div>
      `;
    }
  }

  // Store class on window for Shopify theme editor
  window.ReferencesMap = ReferencesMap;

  // Initialize all instances
  function initMaps() {
    document.querySelectorAll('.references-map-section').forEach(section => {
      const sectionId = section.dataset.sectionId;
      if (sectionId && !section.dataset.initialized) {
        section.dataset.initialized = 'true';
        new ReferencesMap(sectionId);
      }
    });
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMaps);
  } else {
    initMaps();
  }

  // Shopify Section Events (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector('.references-map-section');
      if (section) {
        section.dataset.initialized = '';
        new ReferencesMap(section.dataset.sectionId);
      }
    });
  }
})();
